import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import bizApi, { apiError } from "@/lib/businessApi";
import { inputCls } from "@/components/booking/BookingFields";
import { useLang } from "@/lib/i18n";
import { useBiz } from "@/lib/businessI18n";

const ROLE_STYLES = {
  owner: "bg-miles-deep text-white",
  admin: "bg-miles-deep text-white",
  concierge: "bg-miles-ice text-miles",
  booker: "bg-miles-ice text-miles",
  front_desk: "bg-sky-50 text-sky-700",
  event_manager: "bg-miles-ice text-miles",
  finance: "bg-amber-50 text-amber-600",
  viewer: "bg-ink/[0.05] text-slate",
};
const ALL_ROLES = ["owner", "admin", "concierge", "front_desk", "event_manager", "finance", "viewer"];

export default function Team({ currentUser }) {
  const { t } = useLang();
  const { t: tb } = useBiz();
  const [members, setMembers] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "concierge" });
  const [busy, setBusy] = useState(false);

  const load = () => bizApi.get("/team").then((r) => setMembers(r.data)).catch(() => setMembers([]));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const formValid = form.name.trim().length >= 2 && /.+@.+\..+/.test(form.email) && form.password.length >= 8;

  const add = async () => {
    if (!formValid || busy) return;
    setBusy(true);
    try {
      await bizApi.post("/team", { name: form.name.trim(), email: form.email.trim(), password: form.password, role: form.role });
      toast.success(t("bz.tm.added_t"));
      setForm({ name: "", email: "", password: "", role: "booker" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(apiError(err, t("bz.tm.err")));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (member) => {
    try {
      await bizApi.delete(`/team/${member.id}`);
      setMembers((list) => list.filter((m) => m.id !== member.id));
    } catch (err) {
      toast.error(apiError(err, t("bz.tm.err")));
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid="team-panel">
      <div className="rounded-3xl border border-ink/5 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 p-5 pb-4">
          <div>
            <p className="text-sm font-bold text-ink">{t("bz.tm.t")}</p>
            <p className="mt-0.5 text-xs text-slate">{t("bz.tm.s")}</p>
          </div>
          <button type="button" onClick={() => setShowForm((v) => !v)} data-testid="team-add-toggle"
            className="inline-flex items-center gap-2 rounded-full bg-miles px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-miles-dark">
            <Plus className="h-3.5 w-3.5" strokeWidth={2} /> {t("bz.tm.add")}
          </button>
        </div>

        {showForm && (
          <div className="border-t border-ink/5 p-5" data-testid="team-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <input value={form.name} onChange={set("name")} placeholder={t("bz.auth.name")} data-testid="team-name" className={inputCls} />
              <input type="email" value={form.email} onChange={set("email")} placeholder={t("bz.auth.email")} data-testid="team-email" className={inputCls} />
              <input type="password" value={form.password} onChange={set("password")} placeholder={t("bz.auth.password")} data-testid="team-password" className={inputCls} />
              <select value={form.role} onChange={set("role")} data-testid="team-role"
                className={`${inputCls} appearance-none`}>
                {ALL_ROLES.map((role) => (
                  <option key={role} value={role} data-testid={`team-role-${role}`}>{tb(`team.role.${role}`)}</option>
                ))}
              </select>
            </div>
            <div className="mt-4 flex gap-3">
              <button type="button" onClick={add} disabled={!formValid || busy} data-testid="team-save"
                className="inline-flex items-center gap-2 rounded-full bg-miles px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-miles-dark disabled:opacity-50">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />} {t("bz.tm.save")}
              </button>
              <button type="button" onClick={() => setShowForm(false)} data-testid="team-cancel"
                className="rounded-full border border-ink/10 px-5 py-2.5 text-xs font-semibold text-ink">
                {t("bz.common.cancel")}
              </button>
            </div>
          </div>
        )}

        {!members ? (
          <div className="flex justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-miles" strokeWidth={1.5} /></div>
        ) : (
          members.map((member) => (
            <div key={member.id} className="flex flex-wrap items-center gap-4 border-t border-ink/5 px-5 py-4"
              data-testid={`team-member-${member.email}`}>
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-miles-ice text-xs font-bold text-miles">
                {member.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-ink">
                  {member.name}
                  {member.id === currentUser.id && <span className="ml-2 text-xs font-medium text-slate">({t("bz.tm.you")})</span>}
                </p>
                <p className="truncate text-xs text-slate">{member.email}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-[11px] font-bold ${ROLE_STYLES[member.role] || ROLE_STYLES.viewer}`}
                data-testid={`team-role-badge-${member.email}`}>
                {tb(`team.role.${member.role}`)}
              </span>
              {member.id !== currentUser.id && (
                <select value={member.role} data-testid={`team-role-edit-${member.email}`}
                  onChange={async (e) => {
                    try {
                      await bizApi.patch(`/team/${member.id}`, { role: e.target.value });
                      toast.success(tb("team.saved"));
                      load();
                    } catch (err) { toast.error(apiError(err, t("bz.tm.err"))); }
                  }}
                  className="h-8 rounded-lg border border-ink/10 bg-white px-2 text-[11px] text-slate outline-none focus:border-miles">
                  {ALL_ROLES.map((role) => <option key={role} value={role}>{tb(`team.role.${role}`)}</option>)}
                </select>
              )}
              {member.id !== currentUser.id && (
                <button type="button" onClick={() => remove(member)} data-testid={`team-delete-${member.email}`} title={t("bz.tm.del")}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-ink/10 text-slate transition-colors hover:border-red-300 hover:text-red-500">
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </motion.div>
  );
}
